/**
 * useRFPJobStream hook for monitoring RFP document processing jobs
 * Similar to useProposalTemplateStream but for RFP document ingest/structure extraction
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { streamRFPJobProgress, RFPJobStreamCallbacks } from '../api/opportunities';

export type RFPJobStage = 
  | 'idle'
  | 'queued'
  | 'waiting'
  | 'parsing'
  | 'chunking'
  | 'indexing'
  | 'extracting'
  | 'processing'
  | 'generating'
  | 'complete'
  | 'error';

export interface RFPJobStreamState {
  stage: RFPJobStage;
  message: string;
  progress: number;
  jobStatus: string;
  jobType: string;
  error: string | null;
  isStreaming: boolean;
  elapsed: number;
  jobId: string | null;
}

const initialStreamingState: RFPJobStreamState = {
  stage: 'idle',
  message: '',
  progress: 0,
  jobStatus: '',
  jobType: '',
  error: null,
  isStreaming: false,
  elapsed: 0,
  jobId: null,
};

/**
 * Hook for streaming RFP document processing job progress with real-time updates
 */
export function useRFPJobStream() {
  const [state, setState] = useState<RFPJobStreamState>(initialStreamingState);
  const abortRef = useRef<(() => void) | null>(null);
  const isStartingRef = useRef(false);
  const startTimeRef = useRef<number>(0);
  const elapsedIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update elapsed time during streaming
  useEffect(() => {
    if (!state.isStreaming) {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
      return;
    }

    elapsedIntervalRef.current = setInterval(() => {
      if (startTimeRef.current > 0) {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        setState(prev => ({
          ...prev,
          elapsed: Math.round(elapsed * 10) / 10, // Round to 1 decimal
        }));
      }
    }, 100); // Update every 100ms for smooth elapsed time

    return () => {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
    };
  }, [state.isStreaming]);

  const startStream = useCallback((
    opportunityId: string,
    jobId: string,
    projectId: string
  ) => {
    // Cancel any existing stream first
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }

    // Prevent duplicate concurrent requests
    if (isStartingRef.current) {
      return;
    }

    isStartingRef.current = true;
    startTimeRef.current = Date.now();

    // Reset state
    setState({
      ...initialStreamingState,
      isStreaming: true,
      stage: 'queued',
      message: 'Starting job monitoring...',
      jobId: jobId,
      progress: 0,
    });

    // Start streaming
    const callbacks: RFPJobStreamCallbacks = {
      onStatus: (data) => {
        console.log('[useRFPJobStream] Status update received:', data);
        setState(prev => {
          const newState = {
            ...prev,
            stage: (data.stage as RFPJobStage) || 'processing',
            message: data.message || prev.message,
            progress: Math.max(0, Math.min(100, data.progress || prev.progress)), // Clamp between 0-100
            jobStatus: data.job_status || prev.jobStatus,
            jobType: data.job_type || prev.jobType,
            elapsed: data.elapsed || prev.elapsed,
          };
          console.log('[useRFPJobStream] State updated:', { 
            stage: newState.stage, 
            progress: newState.progress, 
            message: newState.message 
          });
          return newState;
        });
      },

      onComplete: (data) => {
        setState(prev => ({
          ...prev,
          stage: 'complete',
          message: data.message || 'Job completed successfully',
          progress: 100,
          isStreaming: false,
          elapsed: data.elapsed || prev.elapsed,
        }));
        abortRef.current = null;
        isStartingRef.current = false;
        if (elapsedIntervalRef.current) {
          clearInterval(elapsedIntervalRef.current);
          elapsedIntervalRef.current = null;
        }
      },

      onError: (error: Error) => {
        setState(prev => ({
          ...prev,
          stage: 'error',
          error: error.message,
          message: `Error: ${error.message}`,
          isStreaming: false,
        }));
        abortRef.current = null;
        isStartingRef.current = false;
        if (elapsedIntervalRef.current) {
          clearInterval(elapsedIntervalRef.current);
          elapsedIntervalRef.current = null;
        }
      },
    };

    (async () => {
      try {
        const abort = await streamRFPJobProgress(opportunityId, jobId, projectId, callbacks);
        abortRef.current = abort;
      } catch (error: any) {
        setState(prev => ({
          ...prev,
          stage: 'error',
          error: error.message,
          message: `Failed to start monitoring: ${error.message}`,
          isStreaming: false,
        }));
        isStartingRef.current = false;
      }
    })();
  }, []);

  const cancelStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }
    isStartingRef.current = false;
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
    setState(prev => ({
      ...prev,
      isStreaming: false,
      stage: 'idle',
      message: 'Job monitoring cancelled',
    }));
  }, []);

  const reset = useCallback(() => {
    cancelStream();
    isStartingRef.current = false;
    startTimeRef.current = 0;
    setState(initialStreamingState);
  }, [cancelStream]);

  return {
    ...state,
    startStream,
    cancelStream,
    reset,
  };
}
